import { useEffect, useId, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAddressCard,
  faBuilding,
  faChevronDown,
  faGear,
  faRightFromBracket,
  faShield,
} from '@fortawesome/free-solid-svg-icons';
import { Avatar } from './Avatar';

/**
 * User Profile P1 — the header account menu shared by the tenant and platform
 * headers: `role="menu"`, Escape and outside-click close, focus returned to the
 * trigger, 44 px targets on touch, and the photo avatar when one exists — the
 * behaviour the customer-portal menu already had.
 *
 * Presentation only. Which links appear is decided by the caller from its own
 * store / permissions; nothing here authorizes anything.
 */
export interface AccountMenuProps {
  displayName: string;
  firstName?: string | null;
  lastName?: string | null;
  photoUrl?: string | null;
  email?: string | null;
  username?: string | null;
  /** D-100 — a declared shared mailbox: lead with the username, label the address. */
  isSharedEmailAccount?: boolean;
  workspaceName?: string | null;
  workspaceSlug?: string | null;
  roleName?: string | null;
  onProfile: () => void;
  /** Rendered only when provided (the caller has already checked the permission). */
  onSettings?: () => void;
  onLogout: () => void;
}

export function AccountMenu({
  displayName,
  firstName,
  lastName,
  photoUrl,
  email,
  username,
  isSharedEmailAccount = false,
  workspaceName,
  workspaceSlug,
  roleName,
  onProfile,
  onSettings,
  onLogout,
}: AccountMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = (action: () => void) => {
    setOpen(false);
    action();
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex min-h-11 items-center gap-2 rounded-lg px-2 text-sm text-foreground transition-colors hover:bg-primary/[0.07] sm:min-h-9"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={menuId}
        aria-label={`Account menu for ${displayName}`}
      >
        <Avatar firstName={firstName} lastName={lastName} src={photoUrl} size="sm" alt="" />
        <span className="hidden max-w-[120px] truncate font-medium sm:block">{displayName}</span>
        <FontAwesomeIcon icon={faChevronDown} className="h-3 w-3 text-muted-foreground" />
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label="Account"
          className="absolute right-0 top-full z-50 mt-1 w-64 rounded-xl border border-border bg-card shadow-lg"
        >
          <div className="border-b border-border px-4 py-3">
            <p className="text-sm font-medium text-foreground">{displayName}</p>
            {isSharedEmailAccount ? (
              <>
                {username && (
                  <p
                    data-testid="header-identity-username"
                    className="truncate text-xs font-medium text-muted-foreground"
                  >
                    @{username}
                  </p>
                )}
                {email && (
                  <p className="truncate text-xs text-muted-foreground">
                    {email} <span className="opacity-70">(shared mailbox)</span>
                  </p>
                )}
              </>
            ) : (
              <>
                {email && <p className="truncate text-xs text-muted-foreground">{email}</p>}
                {username && <p className="text-xs text-muted-foreground">@{username}</p>}
              </>
            )}
          </div>

          {(workspaceName || roleName) && (
            <div className="space-y-1.5 border-b border-border px-4 py-3">
              {workspaceName && (
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faBuilding} className="h-3 w-3 text-muted-foreground" />
                  <div>
                    <p className="text-xs font-medium text-foreground">{workspaceName}</p>
                    {workspaceSlug && (
                      <p className="text-xs text-muted-foreground">Workspace: {workspaceSlug}</p>
                    )}
                  </div>
                </div>
              )}
              {roleName && (
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faShield} className="h-3 w-3 text-primary" />
                  <p className="text-xs text-foreground">
                    Role: <span className="font-medium text-primary">{roleName}</span>
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="p-1">
            <button
              type="button"
              role="menuitem"
              onClick={() => choose(onProfile)}
              className="flex min-h-11 w-full items-center gap-2.5 rounded-lg px-3 text-sm text-foreground transition-colors hover:bg-muted sm:min-h-9"
            >
              <FontAwesomeIcon icon={faAddressCard} className="h-3.5 w-3.5 text-muted-foreground" />
              My Profile
            </button>
            {onSettings && (
              <button
                type="button"
                role="menuitem"
                onClick={() => choose(onSettings)}
                className="flex min-h-11 w-full items-center gap-2.5 rounded-lg px-3 text-sm text-foreground transition-colors hover:bg-muted sm:min-h-9"
              >
                <FontAwesomeIcon icon={faGear} className="h-3.5 w-3.5 text-muted-foreground" />
                Settings
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              onClick={() => choose(onLogout)}
              className="flex min-h-11 w-full items-center gap-2.5 rounded-lg px-3 text-sm text-destructive transition-colors hover:bg-destructive/10 sm:min-h-9"
            >
              <FontAwesomeIcon icon={faRightFromBracket} className="h-3.5 w-3.5" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
